import React, { useEffect, useState } from 'react'
import moment from 'moment'

// mui components
import { makeStyles } from '@material-ui/core/styles'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import { FormLabel } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import AttachmentComponent from '../form/AttachmentComponent'
import { formatPhoneNumber, isValidUrl } from '../../lib/Global'
import { SITE_INFO_FIELDS } from '../../lib/Constants'
import { useSelector } from 'react-redux'

const useStyles = makeStyles(theme => ({
  root: {
    maxWidth: 345,
    padding: '9px',
    boxShadow: '6px 9px 43px ' + theme.colors.workOrders.detailsCardBorderColor
  },
  field: {
    marginLeft: '5px',
    marginBottom: '5px',
    color: theme.colors.text,
    fontSize: '12px',
    fontWeight: '700'
  },
  fieldData: {
    marginLeft: '5px',
    marginBottom: '30px',
    color: theme.colors.text,
    fontSize: '14px',
    fontWeight: '400',
    lineHeight: '17px',
    letterSpacing: '1px',
    whiteSpace: 'pre-line'
  },
  alignment: {
    paddingLeft: '5px'
  }
}))

export const WoDetails = props => {
  const { workOrder, attachments, serviceAppointments } = props
  const [nteValue, setNteValue] = useState()
  const classes = useStyles()
  const { t } = useTranslation()
  const columnsConfig = useSelector(
    state => state.auth?.user?.userInfo?.configurations?.portals?.subcontractor
      ?.workorders?.columns
  )
  const customLabelsConfig = useSelector(
    state => state.auth?.user?.userInfo?.configurations?.wo_configs?.custom_labels
  )
  const displayContactInfo = useSelector(
    state => state.auth?.user?.userInfo?.configurations?.portals?.subcontractor
      ?.workorders?.display_contact_info
  ) ?? true
  const displayOwnerInfo = useSelector(
    state => state.auth?.user?.userInfo?.configurations?.portals?.subcontractor
      ?.workorders?.display_owner_info
  )

  useEffect(() => {
    let nte = 0
    serviceAppointments.forEach(loc => {
      if (loc.nte) {
        nte += loc.nte
      }
    })
    setNteValue(nte)
  }, [serviceAppointments])

  return (
    <Card className={classes.root}>
      <CardContent
        style={{ paddingBottom: '3px' }}
        className={classes.alignment}
      >
        <FormLabel component="legend" className={classes.field}>
          {(() => {
            const column = columnsConfig?.find(col => col.field === 'site_name')
            return column?.label || t('work_orders.wo_details.location')
          })()}
        </FormLabel>
        <FormLabel component="legend" className={classes.fieldData}>
          {workOrder?.store_name}
        </FormLabel>
        <FormLabel component="legend" className={classes.field}>
          {t('work_orders.wo_details.address')}
        </FormLabel>
        <FormLabel component="legend" className={classes.fieldData}>
          {workOrder?.address}
        </FormLabel>
        {workOrder?.site_information && workOrder?.site_information?.length > 0 && (
          <>
            {workOrder.site_information.filter(info => info.label !== SITE_INFO_FIELDS.LEVEL_NOTES)
            .map((info, index) => {
              return (
                <>
                  {isValidUrl(info.value) ? (
                    <>
                      <FormLabel component="legend" className={classes.field}>
                        <a
                          href={info.value}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {info.label}
                        </a>
                      </FormLabel>
                      <FormLabel component="legend" className={classes.fieldData}>
                      </FormLabel>
                    </>
                  ) : (
                    <>
                      <FormLabel component="legend" className={classes.field}>
                        {info.label}
                      </FormLabel>
                      <FormLabel component="legend" className={classes.fieldData}>
                        {info.value}
                      </FormLabel>
                    </>
                  )}
                </>
              )
            })}
          </>
        )}
        <FormLabel component="legend" className={classes.field}>
          {t('work_orders.wo_details.nte')}
        </FormLabel>
        <FormLabel component="legend" className={classes.fieldData}>
          {nteValue ?? t('general.labels.not_available')}
        </FormLabel>
        <FormLabel component="legend" className={classes.field}>
          {t('work_orders.wo_details.priority')}
        </FormLabel>
        <FormLabel component="legend" className={classes.fieldData}>
          {workOrder?.priority}
        </FormLabel>
        <FormLabel component="legend" className={classes.field}>
          {customLabelsConfig?.open_date || t('work_orders.wo_details.start_date')}
        </FormLabel>
        <FormLabel component="legend" className={classes.fieldData}>
          {moment(new Date(workOrder?.open_date * 1000)).format(
            t('general.format.short_date_time')
          )}
        </FormLabel>
        <FormLabel component="legend" className={classes.field}>
          {customLabelsConfig?.expiration_date || t('work_orders.wo_details.end_date')}
        </FormLabel>
        <FormLabel component="legend" className={classes.fieldData}>
          {workOrder?.expiration_date === 0
            ? ''
            : moment(new Date(workOrder?.expiration_date * 1000)).format(
                t('general.format.short_date_time')
              )}
        </FormLabel>
        <FormLabel component="legend" className={classes.field}>
          {t('work_orders.wo_details.trade')}
        </FormLabel>
        <FormLabel component="legend" className={classes.fieldData}>
          {workOrder?.category}
        </FormLabel>
        <FormLabel component="legend" className={classes.field}>
          {t('work_orders.wo_details.services')}
        </FormLabel>
        <FormLabel component="legend" className={classes.fieldData}>
          {workOrder?.services?.map(item => item.name).join(', ')}
        </FormLabel>

        {displayOwnerInfo && workOrder?.owner &&
          workOrder.owner.name !== 'Integration User' &&
          (workOrder.owner?.name || workOrder.owner?.email || workOrder.owner?.phone) && (
            <>
              <FormLabel component="legend" className={classes.field}>
                {t('work_orders.wo_details.customer_contact')}
              </FormLabel>
              <FormLabel component="legend" className={classes.fieldData}>
                {(workOrder.owner.name || workOrder.owner.phone) && (
                  <>
                    {workOrder.owner?.name && workOrder.owner.name}
                    {workOrder.owner?.name && workOrder.owner?.phone && ' '}
                    {workOrder.owner?.phone && `${formatPhoneNumber(workOrder.owner.phone)}`}
                    <br />
                  </>
                )}
                {workOrder.owner?.email && workOrder.owner.email}
              </FormLabel>
            </>
        )}

        {displayContactInfo && workOrder?.contact_person &&
          (workOrder.contact_person[0] || workOrder.contact_person[1] || workOrder.contact_person[2]) && (
            <>
              <FormLabel component="legend" className={classes.field}>
                {t('work_orders.wo_details.location_contact')}
              </FormLabel>
              <FormLabel component="legend" className={classes.fieldData}>
                {(workOrder.contact_person[0] || workOrder.contact_person[1]) && (
                  <>
                    {workOrder.contact_person[0] && workOrder.contact_person[0]}
                    {workOrder.contact_person[0] && workOrder.contact_person[1] && ' '}
                    {workOrder.contact_person[1] && `${formatPhoneNumber(workOrder.contact_person[1])}`}
                    <br />
                  </>
                )}
                {workOrder.contact_person[2] && workOrder.contact_person[2]}
              </FormLabel>
            </>
        )}

        {workOrder?.notes && (
          <>
            <FormLabel component="legend" className={classes.field}>
              {t('work_orders.wo_details.notes')}
            </FormLabel>
            <FormLabel component="legend" className={classes.fieldData}>
              {workOrder?.notes}
            </FormLabel>
          </>
        )}
        {workOrder?.general_notes && (
          <>
            <FormLabel component="legend" className={classes.field}>
              {t('work_orders.wo_details.general_notes')}
            </FormLabel>
            <FormLabel component="legend" className={classes.fieldData}>
              {workOrder?.general_notes}
            </FormLabel>
          </>
        )}
        {workOrder?.instructions && (
          <>
            <FormLabel component="legend" className={classes.field}>
              {t('work_orders.wo_details.instructions')}
            </FormLabel>
            <FormLabel component="legend" className={classes.fieldData}>
              {workOrder?.instructions}
            </FormLabel>
          </>
        )}
        {attachments && attachments.length > 0 && (
          <>
            <FormLabel component="legend" className={classes.field}>
              {t('work_orders.wo_details.attachments')}
            </FormLabel>
            <AttachmentComponent attachments={attachments} />
          </>
        )}
      </CardContent>
    </Card>
  )
}
